import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { BUG_REPORT_BASE_PATH, BugReportService, BugReportType, BugSeverity } from '../services/bug-report.service';

/**
 * Floating "report a bug" launcher + modal, mounted once in the app shell so it's reachable
 * from every page. Captures a screenshot (html2canvas) on open and ships the report to
 * usermanager → Notion + Slack via {@link BugReportService}.
 *
 * <p>Promoted to libs/shared-ui so both wheere-integrator and wheere-customer mount the same
 * component. The consuming app is responsible for providing {@link USERMANAGER_BASE_PATH}
 * (from {@code @wheere-front/domain-users}) and, optionally, {@link BUG_REPORT_APP_NAME}.
 *
 * <p>All copy comes from Transloco keys under {@code bugReport.*}: button, title, type,
 * types.BUG/IDEA, severity, severities.LOW/MEDIUM/HIGH, fieldTitle, titlePlaceholder,
 * fieldDescription, descriptionPlaceholder, attachScreenshot, chooseImage, attachLogs,
 * capturing, cancel, submit, successSummary, successDetail, errorDetail. Translate them in
 * each app's i18n bundle.
 */
@Component({
  selector: 'lib-bug-report-button',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslocoModule],
  template: `
    @if (enabled) {
      <!-- Launcher -->
      <button
        type="button"
        class="bg-eletricPurple-500 hover:bg-eletricPurple-600 fixed bottom-4 right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full text-white shadow-lg transition-all hover:scale-105"
        [title]="'bugReport.button' | transloco"
        (click)="open()">
        <svg class="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
      </button>
    }

    @if (visible()) {
      <!-- Backdrop -->
      <div class="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center" (click)="close()">
        <!-- Panel -->
        <div
          class="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-800"
          (click)="$event.stopPropagation()">
          <header class="flex items-center justify-between border-b border-gray-100 px-5 py-3.5 dark:border-slate-700">
            <h2 class="wh-display text-eletricPurple-500 text-base font-bold">
              {{ 'bugReport.title' | transloco }}
            </h2>
            <button type="button" class="text-gray-400 hover:text-gray-700 dark:hover:text-slate-200" (click)="close()">
              <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div class="flex flex-col gap-4 overflow-y-auto px-5 py-4">
            <!-- Type -->
            <div class="flex flex-col gap-1.5">
              <span class="wh-label">{{ 'bugReport.type' | transloco }}</span>
              <div
                class="flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-slate-600 dark:bg-slate-700/40">
                @for (t of types; track t) {
                  <button
                    type="button"
                    class="flex-1 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors"
                    [class.bg-white]="type() === t"
                    [class.text-eletricPurple-600]="type() === t"
                    [class.shadow-sm]="type() === t"
                    [class.text-gray-500]="type() !== t"
                    [class.dark:bg-slate-800]="type() === t"
                    (click)="type.set(t)">
                    {{ 'bugReport.types.' + t | transloco }}
                  </button>
                }
              </div>
            </div>

            <!-- Severity -->
            <div class="flex flex-col gap-1.5">
              <span class="wh-label">{{ 'bugReport.severity' | transloco }}</span>
              <div
                class="flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-slate-600 dark:bg-slate-700/40">
                @for (s of severities; track s) {
                  <button
                    type="button"
                    class="flex-1 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors"
                    [class.bg-white]="severity() === s"
                    [class.text-eletricPurple-600]="severity() === s"
                    [class.shadow-sm]="severity() === s"
                    [class.text-gray-500]="severity() !== s"
                    [class.dark:bg-slate-800]="severity() === s"
                    (click)="severity.set(s)">
                    {{ 'bugReport.severities.' + s | transloco }}
                  </button>
                }
              </div>
            </div>

            <!-- Title -->
            <label class="flex flex-col gap-1.5">
              <span class="wh-label">{{ 'bugReport.fieldTitle' | transloco }}</span>
              <input
                class="wh-bug-input"
                type="text"
                [placeholder]="'bugReport.titlePlaceholder' | transloco"
                [ngModel]="title()"
                (ngModelChange)="title.set($event)" />
            </label>

            <!-- Description -->
            <label class="flex flex-col gap-1.5">
              <span class="wh-label">{{ 'bugReport.fieldDescription' | transloco }}</span>
              <textarea
                class="wh-bug-input"
                rows="4"
                [placeholder]="'bugReport.descriptionPlaceholder' | transloco"
                [ngModel]="description()"
                (ngModelChange)="description.set($event)"></textarea>
            </label>

            <!-- Screenshot -->
            <div class="flex flex-col gap-1.5">
              <label class="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-slate-200">
                <input type="checkbox" [ngModel]="attachScreenshot()" (ngModelChange)="attachScreenshot.set($event)" />
                {{ 'bugReport.attachScreenshot' | transloco }}
                @if (capturing()) {
                  <span class="text-xs text-gray-400">· {{ 'bugReport.capturing' | transloco }}</span>
                }
              </label>
              @if (attachScreenshot() && screenshot()) {
                <img
                  [src]="screenshot()!"
                  alt="screenshot"
                  class="max-h-40 rounded-lg border border-gray-200 object-contain dark:border-slate-600" />
              }
              <label
                class="inline-flex w-fit cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50">
                <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M18 12.75h.008v.008H18v-.008zM21 19.5V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v13.5A2.25 2.25 0 005.25 21.75h13.5A2.25 2.25 0 0021 19.5z" />
                </svg>
                {{ 'bugReport.chooseImage' | transloco }}
                <input type="file" accept="image/*" class="hidden" (change)="onFileSelected($event)" />
              </label>
              <label class="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-slate-200">
                <input type="checkbox" [ngModel]="attachLogs()" (ngModelChange)="attachLogs.set($event)" />
                {{ 'bugReport.attachLogs' | transloco }}
              </label>
            </div>

            @if (error()) {
              <p
                class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                {{ error() }}
              </p>
            }
          </div>

          <footer class="flex justify-end gap-2 border-t border-gray-100 px-5 py-3 dark:border-slate-700">
            <button
              type="button"
              class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50"
              (click)="close()">
              {{ 'bugReport.cancel' | transloco }}
            </button>
            <button
              type="button"
              class="bg-eletricPurple-500 hover:bg-eletricPurple-600 inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              [disabled]="!canSubmit()"
              (click)="submit()">
              @if (submitting()) {
                <svg
                  class="size-4 animate-spin"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  stroke-width="2.5">
                  <path stroke-linecap="round" d="M4 12a8 8 0 018-8" />
                </svg>
              }
              {{ 'bugReport.submit' | transloco }}
            </button>
          </footer>
        </div>
      </div>
    }
  `,
  styles: [
    `
      .wh-label {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #6b7280;
      }
      :host-context(html.dark) .wh-label {
        color: #94a3b8;
      }
      .wh-bug-input {
        width: 100%;
        border-radius: 0.5rem;
        border: 1px solid #d1d5db;
        background: #fff;
        padding: 0.5rem 0.75rem;
        font-size: 0.875rem;
        color: #111827;
        outline: none;
        transition:
          border-color 0.15s ease,
          box-shadow 0.15s ease;
      }
      .wh-bug-input:focus {
        border-color: #7c3aed;
        box-shadow: 0 0 0 3px rgba(80, 0, 205, 0.14);
      }
      :host-context(html.dark) .wh-bug-input {
        border-color: #475569;
        background-color: #1e293b;
        color: #f1f5f9;
      }
    `,
  ],
})
export class BugReportButtonComponent {
  protected readonly types: BugReportType[] = ['BUG', 'IDEA'];
  protected readonly severities: BugSeverity[] = ['LOW', 'MEDIUM', 'HIGH'];

  protected readonly visible = signal(false);
  protected readonly type = signal<BugReportType>('BUG');
  protected readonly severity = signal<BugSeverity>('MEDIUM');
  protected readonly title = signal('');
  protected readonly description = signal('');
  protected readonly attachScreenshot = signal(true);
  protected readonly attachLogs = signal(true);
  protected readonly screenshot = signal<string | null>(null);
  protected readonly capturing = signal(false);
  protected readonly submitting = signal(false);
  protected readonly error = signal<string | null>(null);

  #service = inject(BugReportService);
  #toast = inject(MessageService);
  #transloco = inject(TranslocoService);

  /** Whether the surrounding app has wired the BUG_REPORT_BASE_PATH provider.
   * When it hasn't (e.g. local dev without usermanager), the launcher button and
   * its dialog are suppressed so the user never sees a button that POSTs nowhere.
   * BugReportService still gets constructed (its factory now defaults to null);
   * the template gate keeps it from ever being invoked in that config. */
  protected readonly enabled: boolean = !!inject(BUG_REPORT_BASE_PATH, { optional: true });

  protected readonly canSubmit = computed(
    () => !this.submitting() && this.title().trim().length > 0 && this.description().trim().length > 0
  );

  protected open(): void {
    this.reset();
    this.visible.set(true);
    // Capture after the launcher click settles so the modal isn't in the shot.
    this.capturing.set(true);
    void this.#service.captureScreenshot().then(shot => {
      this.screenshot.set(shot);
      this.capturing.set(false);
    });
  }

  protected close(): void {
    this.visible.set(false);
  }

  /** Manual image attach — reads the picked file as a data URL and uses it as the screenshot. */
  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.screenshot.set(reader.result as string);
      this.attachScreenshot.set(true);
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  protected async submit(): Promise<void> {
    if (!this.canSubmit()) return;
    this.submitting.set(true);
    this.error.set(null);
    try {
      await this.#service.submit({
        type: this.type(),
        severity: this.severity(),
        title: this.title(),
        description: this.description(),
        screenshot: this.attachScreenshot() ? this.screenshot() : null,
        includeLogs: this.attachLogs(),
      });
      this.#toast.add({
        severity: 'success',
        summary: this.#transloco.translate('bugReport.successSummary'),
        detail: this.#transloco.translate('bugReport.successDetail'),
      });
      this.visible.set(false);
    } catch {
      this.error.set(this.#transloco.translate('bugReport.errorDetail'));
    } finally {
      this.submitting.set(false);
    }
  }

  private reset(): void {
    this.type.set('BUG');
    this.severity.set('MEDIUM');
    this.title.set('');
    this.description.set('');
    this.attachScreenshot.set(true);
    this.attachLogs.set(true);
    this.screenshot.set(null);
    this.error.set(null);
    this.submitting.set(false);
  }
}
